import { on, EVENTS } from '../core/events';
import type { LecturaHostAdapter, ResourceItem, ResourcesStorage, ChatRoleService, AIResourceSyncState, AIResourcesService } from '../core/types';

interface RegisterResourcesOptions {
  slot?: HTMLElement;
  roleService?: ChatRoleService;
  ai?: AIResourcesService;
  /**
   * Remote: when Firestore role is viewer but MQTT session is presenter/display, still show manage UI
   * and the toolbar button (aligned with presentation: presenters always see resources).
   */
  sessionCanManageResources?: () => boolean;
}

declare global {
  interface Window {
    __lecturaToggleResources?: () => void;
  }
}

function escapeHtml(s: string | null | undefined): string {
  if (!s) return '';
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

function formatSize(bytes: number | undefined): string {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function canManage(roleService?: ChatRoleService): boolean {
  const role = roleService?.getRole?.() || 'viewer';
  return role === 'presenter' || role === 'admin';
}

function getHostLabel(url: string | undefined): string {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    return new URL(raw).hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
}

function getSyncChip(state: AIResourceSyncState | undefined): { label: string; className: string; title: string } {
  if (!state) {
    return {
      label: 'Not synced with AI',
      className: 'rf-resource-sync-chip rf-resource-sync-idle',
      title: 'Resource has not been synced with AI yet.'
    };
  }
  if (state.status === 'pending') {
    const attempt = Number.isFinite(Number(state.attempt)) ? ` (attempt ${state.attempt})` : '';
    return {
      label: 'Syncing',
      className: 'rf-resource-sync-chip rf-resource-sync-pending',
      title: `Sync in progress${attempt}`
    };
  }
  if (state.status === 'error') {
    return {
      label: 'Sync failed',
      className: 'rf-resource-sync-chip rf-resource-sync-error',
      title: state.lastError || 'Last sync attempt failed.'
    };
  }
  if (state.status === 'deleted') {
    return {
      label: 'Removed from AI',
      className: 'rf-resource-sync-chip rf-resource-sync-idle',
      title: 'Resource has been removed from AI index.'
    };
  }
  if (state.status === 'stale') {
    return {
      label: 'Not synced with AI',
      className: 'rf-resource-sync-chip rf-resource-sync-stale',
      title: 'Resource changed. Sync with AI to refresh answers.'
    };
  }
  const chunks = Number.isFinite(Number(state.chunkCount)) ? `${state.chunkCount} chunks` : 'AI ready';
  return {
    label: 'AI ready',
    className: 'rf-resource-sync-chip rf-resource-sync-indexed',
    title: chunks
  };
}

function formatSyncAge(ts: number | undefined): string {
  const millis = Number(ts || 0);
  if (!Number.isFinite(millis) || millis <= 0) return '';
  const diffMs = Date.now() - millis;
  if (!Number.isFinite(diffMs) || diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getSyncMeta(state: AIResourceSyncState | undefined): string {
  if (!state) return 'Waiting for first AI sync';
  if (state.status === 'pending') return 'OpenAI indexing in progress';
  if (state.status === 'stale') return 'Content changed since last index - sync needed';
  if (state.status === 'error') return state.lastError ? `Last error: ${state.lastError}` : 'Last sync attempt failed';
  if (state.status === 'deleted') return 'Removed from AI index';
  const parts: string[] = [];
  if (Number.isFinite(Number(state.chunkCount))) parts.push(`${state.chunkCount} chunks`);
  const age = formatSyncAge(state.indexedAt || state.updatedAt);
  if (age) parts.push(`updated ${age}`);
  return parts.length ? parts.join(' - ') : 'Indexed';
}

interface UrlPreviewData {
  normalizedUrl: string;
  title: string;
  description?: string;
  imageUrl?: string;
}

function isValidDomainHost(host: string): boolean {
  if (!host || host.endsWith('.')) return false;
  if (host.length > 253) return false;
  const labels = host.split('.');
  if (labels.length < 2) return false;
  if (!labels.every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label))) return false;
  const tld = labels[labels.length - 1];
  return /^[a-z]{2,63}$/i.test(tld);
}

function normalizeHttpUrl(url: string): string {
  const raw = String(url || '').trim();
  if (!raw) throw new Error('Enter a URL');
  const next = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  const parsed = new URL(next);
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new Error('Only http(s) URLs are allowed');
  }
  const host = String(parsed.hostname || '').trim().toLowerCase();
  const isIpv4 = /^(?:\d{1,3}\.){3}\d{1,3}$/.test(host) && host.split('.').every((p) => Number(p) >= 0 && Number(p) <= 255);
  const isDomain = isValidDomainHost(host);
  if (!host || (!isIpv4 && !isDomain)) {
    throw new Error('Enter a valid URL');
  }
  if (host === 'localhost' || host.endsWith('.local')) {
    throw new Error('Local network URLs are not allowed');
  }
  if (isIpv4) {
    const [a, b] = host.split('.').map((p) => Number(p));
    const privateRange =
      a === 10 ||
      a === 127 ||
      a === 0 ||
      (a === 169 && b === 254) ||
      (a === 192 && b === 168) ||
      (a === 172 && b >= 16 && b <= 31);
    if (privateRange) throw new Error('Private/local IP URLs are not allowed');
  }
  return parsed.toString();
}

function defaultTitleFromUrl(normalizedUrl: string): string {
  const u = new URL(normalizedUrl);
  const host = u.hostname.replace(/^www\./i, '');
  const parts = u.pathname.split('/').filter(Boolean);
  const leaf = parts.length ? decodeURIComponent(parts[parts.length - 1]).replace(/[-_]+/g, ' ') : '';
  return leaf || host || normalizedUrl;
}

function extractMetaContent(doc: Document, selector: string): string {
  const node = doc.querySelector(selector) as HTMLMetaElement | null;
  return String(node?.content || '').trim();
}

async function fetchUrlPreview(rawUrl: string, presentationId: string): Promise<UrlPreviewData> {
  const normalizedUrl = normalizeHttpUrl(rawUrl);
  const fallbackTitle = defaultTitleFromUrl(normalizedUrl);
  const fb = typeof window !== 'undefined' ? (window as { firebase?: unknown }).firebase : null;
  const functionsApi = fb && typeof (fb as { functions?: unknown }).functions === 'function'
    ? (fb as {
        functions: (() => { httpsCallable(name: string): (data?: unknown) => Promise<{ data?: unknown }> }) & {
          region?: (region: string) => { httpsCallable(name: string): (data?: unknown) => Promise<{ data?: unknown }> };
        };
      }).functions
    : null;
  if (functionsApi) {
    try {
      const svc = typeof functionsApi.region === 'function' ? functionsApi.region('us-central1') : functionsApi();
      const call = svc.httpsCallable('fetchUrlMetadata');
      const result = await call({ url: normalizedUrl, presentationId });
      const data = (result && typeof result === 'object' ? (result as { data?: unknown }).data : null) as
        | { normalizedUrl?: unknown; title?: unknown; description?: unknown; imageUrl?: unknown }
        | null;
      if (data && typeof data === 'object') {
        const title = String(data.title || '').trim() || fallbackTitle;
        const description = String(data.description || '').trim();
        const imageUrl = String(data.imageUrl || '').trim();
        return {
          normalizedUrl: normalizeHttpUrl(String(data.normalizedUrl || normalizedUrl)),
          title: title.slice(0, 140),
          ...(description ? { description: description.slice(0, 2000) } : {}),
          ...(imageUrl ? { imageUrl } : {})
        };
      }
    } catch {
      // Fall back to browser metadata lookup when callable is unavailable.
    }
  }
  try {
    const resp = await fetch(normalizedUrl, { method: 'GET' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const contentType = String(resp.headers.get('content-type') || '').toLowerCase();
    if (!contentType.includes('text/html')) {
      return { normalizedUrl, title: fallbackTitle };
    }
    const html = await resp.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const title =
      extractMetaContent(doc, 'meta[property="og:title"]') ||
      String(doc.querySelector('title')?.textContent || '').trim() ||
      fallbackTitle;
    const description =
      extractMetaContent(doc, 'meta[property="og:description"]') ||
      extractMetaContent(doc, 'meta[name="description"]') ||
      '';
    const rawImage = extractMetaContent(doc, 'meta[property="og:image"]') || extractMetaContent(doc, 'meta[name="twitter:image"]');
    let imageUrl = '';
    if (rawImage) {
      try {
        imageUrl = new URL(rawImage, normalizedUrl).toString();
      } catch {
        imageUrl = '';
      }
    }
    return {
      normalizedUrl,
      title: title.slice(0, 140),
      description: description.slice(0, 2000) || undefined,
      imageUrl: imageUrl || undefined
    };
  } catch {
    return { normalizedUrl, title: fallbackTitle };
  }
}

export function registerResources(
  adapter: LecturaHostAdapter,
  storage: ResourcesStorage,
  opts: RegisterResourcesOptions = {}
): void {
  const mountPoint = adapter.getDrawerMountPoint();
  const barSlot = opts.slot;
  const roleService = opts.roleService;
  const ai = opts.ai;
  let allResources: ResourceItem[] = [];
  let visibleResources: ResourceItem[] = [];
  let unsubscribe: (() => void) | null = null;
  let unsubscribeAIState: (() => void) | null = null;
  let aiStateByResourceId: Record<string, AIResourceSyncState> = {};
  let open = false;
  let uploadInFlight = 0;
  let syncInFlight = false;
  let urlComposerOpen = false;
  let editingTitleId: string | null = null;
  const failedFaviconKeys = new Set<string>();

  function isResourceManager(): boolean {
    if (canManage(roleService)) return true;
    try {
      return opts.sessionCanManageResources?.() === true;
    } catch {
      return false;
    }
  }

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'rf-resources-btn lectura-bar-btn lectura-bar-resources-btn';
  btn.setAttribute('aria-label', 'Extra resources');
  btn.title = 'Extra resources (R)';
  btn.innerHTML =
    '<svg class="lectura-bar-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>' +
    '<path d="M14 2v6h6"></path>' +
    '</svg>';
  barSlot?.appendChild(btn);

  const backdrop = document.createElement('div');
  backdrop.className = 'rf-resources-backdrop';
  backdrop.setAttribute('aria-hidden', 'true');
  mountPoint.appendChild(backdrop);

  const drawer = document.createElement('aside');
  drawer.className = 'rf-resources-drawer';
  drawer.setAttribute('aria-hidden', 'true');
  drawer.innerHTML =
    '<div class="rf-resources-header">' +
    '<div class="rf-resources-title-wrap"><h3 class="rf-resources-title">Extra resources</h3><p class="rf-resources-subtitle">Useful links and files for this presentation.</p></div>' +
    '<button type="button" class="rf-resources-close" aria-label="Close resources">&times;</button>' +
    '</div>' +
    '<div class="rf-resources-status rf-hidden"></div>' +
    '<div class="rf-resources-error rf-hidden" role="alert"></div>' +
    '<div class="rf-resources-composer rf-hidden">' +
    '<div class="rf-resources-url-form">' +
    '<input class="rf-resources-url-input" type="url" maxlength="2048" placeholder="https://example.com/resource" />' +
    '<div class="rf-resources-url-preview rf-hidden">' +
    '<img class="rf-resources-url-preview-image rf-hidden" alt="" />' +
    '<div class="rf-resources-url-preview-text">' +
    '<strong class="rf-resources-url-preview-title"></strong>' +
    '<p class="rf-resources-url-preview-desc rf-hidden"></p>' +
    '</div>' +
    '</div>' +
    '<div class="rf-resources-url-actions">' +
    '<button type="button" class="rf-resources-add-url">Save URL</button>' +
    '<button type="button" class="rf-resources-cancel-url">Cancel</button>' +
    '</div>' +
    '</div>' +
    '</div>' +
    '<input class="rf-resources-file-input rf-hidden" type="file" />' +
    '<div class="rf-resources-list"></div>';
  mountPoint.appendChild(drawer);

  const closeBtn = drawer.querySelector('.rf-resources-close') as HTMLButtonElement | null;
  const listEl = drawer.querySelector('.rf-resources-list') as HTMLElement | null;
  const errEl = drawer.querySelector('.rf-resources-error') as HTMLElement | null;
  const statusEl = drawer.querySelector('.rf-resources-status') as HTMLElement | null;
  const composerEl = drawer.querySelector('.rf-resources-composer') as HTMLElement | null;
  const urlInput = drawer.querySelector('.rf-resources-url-input') as HTMLInputElement | null;
  const previewEl = drawer.querySelector('.rf-resources-url-preview') as HTMLElement | null;
  const previewImg = drawer.querySelector('.rf-resources-url-preview-image') as HTMLImageElement | null;
  const previewTitleEl = drawer.querySelector('.rf-resources-url-preview-title') as HTMLElement | null;
  const previewDescEl = drawer.querySelector('.rf-resources-url-preview-desc') as HTMLElement | null;
  const addUrlBtn = drawer.querySelector('.rf-resources-add-url') as HTMLButtonElement | null;
  const cancelUrlBtn = drawer.querySelector('.rf-resources-cancel-url') as HTMLButtonElement | null;
  const fileInput = drawer.querySelector('.rf-resources-file-input') as HTMLInputElement | null;
  let activeUrlPreview: UrlPreviewData | null = null;
  function showConfirmDialog(message: string, okLabel: string, onOk: () => void): void {
    const existing = drawer.querySelector('.rf-chat-delete-confirm');
    if (existing && existing.parentElement) existing.parentElement.removeChild(existing);
    const overlay = document.createElement('div');
    overlay.className = 'rf-chat-delete-confirm';
    overlay.innerHTML =
      '<div class="rf-chat-delete-dialog">' +
      '<p class="rf-chat-delete-text">' + escapeHtml(message) + '</p>' +
      '<div class="rf-chat-delete-actions">' +
      '<button type="button" class="rf-chat-delete-cancel">Cancel</button>' +
      '<button type="button" class="rf-chat-delete-ok">' + escapeHtml(okLabel) + '</button>' +
      '</div>' +
      '</div>';
    drawer.appendChild(overlay);
    const close = (): void => {
      if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
    };
    const cancelBtn = overlay.querySelector('.rf-chat-delete-cancel') as HTMLButtonElement | null;
    const okBtn = overlay.querySelector('.rf-chat-delete-ok') as HTMLButtonElement | null;
    cancelBtn?.addEventListener('click', close);
    okBtn?.addEventListener('click', () => {
      close();
      onOk();
    });
    overlay.addEventListener('click', (ev) => {
      if (ev.target === overlay) close();
    });
  }
  listEl?.addEventListener(
    'error',
    (ev) => {
      const target = ev.target as HTMLElement | null;
      if (!target || !target.classList.contains('rf-resource-favicon')) return;
      const key = String(target.getAttribute('data-favicon-key') || '').trim();
      if (!key || failedFaviconKeys.has(key)) return;
      failedFaviconKeys.add(key);
      renderList();
      bindListActions();
    },
    true
  );
  listEl?.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement | null;
    const link = target?.closest('.rf-resources-item-link') as HTMLAnchorElement | null;
    if (!link) return;
    ev.preventDefault();
    const href = String(link.getAttribute('href') || '').trim();
    if (!href || href === '#') return;
    const resourceType = String(link.getAttribute('data-resource-type') || '').trim();
    const itemEl = link.closest('.rf-resources-item') as HTMLElement | null;
    const itemId = String(itemEl?.getAttribute('data-id') || '').trim();
    const matched = allResources.find((r) => r.id === itemId) || visibleResources.find((r) => r.id === itemId);
    let host = href;
    try {
      host = new URL(href).hostname || href;
    } catch {
      // Keep raw href fallback.
    }
    const isFile = resourceType === 'file';
    const fileLabel = matched?.fileName || matched?.title || 'this file';
    const text = isFile
      ? `You are about to download "${fileLabel}" from this presentation. Continue only if you trust this file.`
      : `You are leaving Lectura and opening an external website (${host}). Continue?`;
    const okLabel = isFile ? 'Download file' : 'Open website';
    showConfirmDialog(text, okLabel, () => {
      window.open(href, '_blank', 'noopener,noreferrer');
    });
  });

  function setComposerPreview(data: UrlPreviewData | null): void {
    activeUrlPreview = data;
    if (!previewEl || !previewTitleEl || !previewDescEl || !previewImg) return;
    if (!data) {
      previewEl.classList.add('rf-hidden');
      previewImg.classList.add('rf-hidden');
      previewImg.removeAttribute('src');
      previewTitleEl.textContent = '';
      previewDescEl.textContent = '';
      previewDescEl.classList.add('rf-hidden');
      return;
    }
    previewEl.classList.remove('rf-hidden');
    previewTitleEl.textContent = data.title;
    if (data.description) {
      previewDescEl.textContent = data.description;
      previewDescEl.classList.remove('rf-hidden');
    } else {
      previewDescEl.textContent = '';
      previewDescEl.classList.add('rf-hidden');
    }
    if (data.imageUrl) {
      previewImg.src = data.imageUrl;
      previewImg.classList.remove('rf-hidden');
    } else {
      previewImg.removeAttribute('src');
      previewImg.classList.add('rf-hidden');
    }
  }

  function showError(message: string): void {
    if (!errEl) return;
    errEl.textContent = message || '';
    errEl.classList.toggle('rf-hidden', !message);
  }

  function setStatus(message: string, tone: 'default' | 'success' | 'warning' = 'default'): void {
    if (!statusEl) return;
    statusEl.textContent = message;
    statusEl.classList.remove('rf-resources-status-default', 'rf-resources-status-success', 'rf-resources-status-warning');
    statusEl.classList.add(
      tone === 'success' ? 'rf-resources-status-success' : tone === 'warning' ? 'rf-resources-status-warning' : 'rf-resources-status-default'
    );
    statusEl.classList.toggle('rf-hidden', !message);
  }

  function updateUploadStatus(): void {
    const pendingSync = Object.values(aiStateByResourceId).filter((s) => s.status === 'pending').length;
    const failedSync = Object.values(aiStateByResourceId).filter((s) => s.status === 'error').length;
    if (uploadInFlight > 0) {
      const uploadText = uploadInFlight === 1 ? 'Uploading resource…' : `Uploading ${uploadInFlight} resources…`;
      const syncText = pendingSync > 0 ? ` OpenAI sync pending: ${pendingSync}` : '';
      setStatus(uploadText + syncText, 'default');
      return;
    }
    if (syncInFlight) {
      setStatus('Syncing resources with AI…', 'default');
      return;
    }
    if (pendingSync > 0) {
      setStatus(`OpenAI sync in progress for ${pendingSync} resource${pendingSync === 1 ? '' : 's'}…`, 'default');
      return;
    }
    if (failedSync > 0) {
      setStatus(
        `AI sync limit or failure for ${failedSync} resource${failedSync === 1 ? '' : 's'}. Check resource sync labels for details.`,
        'warning'
      );
      return;
    }
    setStatus('', 'default');
  }

  function setUrlComposerOpen(next: boolean): void {
    urlComposerOpen = next;
    composerEl?.classList.toggle('rf-hidden', !urlComposerOpen);
    if (next) {
      window.setTimeout(() => urlInput?.focus(), 0);
    } else {
      if (urlInput) urlInput.value = '';
      setComposerPreview(null);
    }
  }

  function setOpen(next: boolean): void {
    open = next;
    drawer.classList.toggle('rf-open', open);
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    backdrop.classList.toggle('rf-open', open);
    backdrop.setAttribute('aria-hidden', open ? 'false' : 'true');
    btn.classList.toggle('lectura-bar-btn-active', open);
    if (!open) setUrlComposerOpen(false);
  }

  function getSorted(items: ResourceItem[]): ResourceItem[] {
    return items.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  function collectSlidesPlainText(): string {
    try {
      const container = adapter.getContainer?.();
      const root = (container && container.closest('.reveal')) || document.querySelector('.reveal');
      if (!root) return '';
      const slidesRoot = root.querySelector('.slides');
      if (!slidesRoot) return '';
      const sections = Array.from(slidesRoot.querySelectorAll<HTMLElement>('section'));
      if (!sections.length) return '';
      const parts: string[] = [];
      sections.forEach((section, idx) => {
        const mainText = String(section.innerText || '').trim();
        const notesEls = Array.from(section.querySelectorAll<HTMLElement>('aside.notes'));
        const notesText = notesEls
          .map((n) => String(n.innerText || '').trim())
          .filter(Boolean)
          .join('\n\n');
        if (!mainText && !notesText) return;
        const slideNum = idx + 1;
        const chunks: string[] = [];
        if (mainText) chunks.push(mainText);
        if (notesText) chunks.push(`Notes:\n${notesText}`);
        parts.push(`Slide ${slideNum}:\n${chunks.join('\n\n')}`);
      });
      return parts.join('\n\n');
    } catch {
      return '';
    }
  }

  function renderList(): void {
    if (!listEl) return;
    const items = isResourceManager() ? allResources : visibleResources;
    const cards = getSorted(items)
      .map((item) => {
        const canEdit = isResourceManager();
        const isEditingTitle = canEdit && editingTitleId === item.id;
        const href = item.url || '#';
        const meta =
          item.type === 'file'
            ? [item.fileName || 'File', formatSize(item.sizeBytes)].filter(Boolean).join(' · ')
            : getHostLabel(href);
        const typeEmoji = item.type === 'file' ? '📄' : '🔗';
        const iconOrFavicon =
          item.type === 'url' && href && href !== '#' && !failedFaviconKeys.has(href)
            ? '<img class="rf-resource-favicon" src="https://www.google.com/s2/favicons?sz=64&domain_url=' +
              encodeURIComponent(href) +
              '" data-favicon-key="' + escapeHtml(href) + '" alt="" loading="lazy" referrerpolicy="no-referrer" />'
            : '<span class="rf-resource-type-emoji" aria-hidden="true">' + typeEmoji + '</span>';
        const titleHtml = isEditingTitle
          ? '<div class="rf-resource-inline-edit">' +
            '<input type="text" class="rf-resource-title-input" maxlength="140" value="' + escapeHtml(item.title) + '" />' +
            '<button type="button" class="rf-resource-title-save" title="Save title">✓</button>' +
            '<button type="button" class="rf-resource-title-cancel" title="Cancel edit">×</button>' +
            '</div>'
          : '<div class="rf-resource-title-row">' +
            iconOrFavicon +
            '<a class="rf-resources-item-link" data-resource-type="' + escapeHtml(item.type) + '" href="' + escapeHtml(href) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(item.title) + '</a>' +
            (canEdit ? '<button type="button" class="rf-resource-edit-title-icon" title="Edit title">✎</button>' : '') +
            '</div>';
        const sync = canEdit ? getSyncChip(aiStateByResourceId[item.id]) : null;
        const syncMeta = canEdit ? getSyncMeta(aiStateByResourceId[item.id]) : '';
        return (
          '<div class="rf-resources-item" data-id="' +
          escapeHtml(item.id) +
          '">' +
          '<div class="rf-resources-item-main">' +
          titleHtml +
          (item.description ? '<p class="rf-resources-item-desc">' + escapeHtml(item.description) + '</p>' : '') +
          (meta ? '<span class="rf-resources-item-meta">' + escapeHtml(meta) + '</span>' : '') +
          '</div>' +
          (canEdit
            ? '<div class="rf-resources-item-actions">' +
              '<label class="rf-resources-item-visible">' +
              '<input type="checkbox" class="rf-resource-toggle-visible" ' +
              (item.isVisible ? 'checked' : '') +
              ' />' +
              '<span class="rf-resource-switch"><span class="rf-resource-switch-thumb"></span></span><span class="rf-resources-item-visible-label">' +
              (item.isVisible ? 'Visible' : 'Hidden') +
              '</span></label>' +
              (sync
                ? '<span class="' +
                  escapeHtml(sync.className) +
                  ' rf-resource-sync-inline" title="' +
                  escapeHtml(syncMeta || sync.title) +
                  '">' +
                  escapeHtml(sync.label) +
                  '</span>'
                : '') +
              '<button type="button" class="rf-resource-move-up" title="Move up">↑</button>' +
              '<button type="button" class="rf-resource-move-down" title="Move down">↓</button>' +
              '<button type="button" class="rf-resource-remove-icon" title="Remove resource">&times;</button>' +
              '</div>'
            : '') +
          '</div>'
        );
      })
      .join('');

    const adminNotice = isResourceManager()
      ? '<div class="rf-resources-admin-notice">' +
          '<span class="rf-resources-admin-notice-icon" aria-hidden="true">AI</span>' +
          '<div class="rf-resources-admin-notice-content">' +
          '<p><strong>AI-ready resources:</strong> viewers can ask questions in chat to AI about these materials. Add only content you are comfortable sharing for AI-assisted learning and possible model training use.</p>' +
          '<p class="rf-resources-admin-insights"><span class="rf-resources-admin-quota rf-resources-admin-quota-loading">Loading AI usage…</span></p>' +
          '<button type="button" class="rf-resources-sync-ai-btn"' + (syncInFlight ? ' disabled' : '') + '>Sync resources with AI</button>' +
          '</div>' +
        '</div>'
      : '';
    const viewerNotice = !isResourceManager() && items.length
      ? '<div class="rf-resources-viewer-notice">' +
          '<span class="rf-resources-viewer-notice-icon" aria-hidden="true">?</span>' +
          '<p><strong>Did you know?</strong> You can ask questions about these materials in chat using <code>@ai</code>.</p>' +
        '</div>'
      : '';
    const adminCards = isResourceManager()
      ? '<button type="button" class="rf-resources-item rf-resources-item-add" data-action="add-url">' +
          '<span class="rf-resources-item-add-icon">+</span>' +
          '<span class="rf-resources-item-add-text"><strong>Add website / URL</strong><em>Link extra materials for this presentation.</em></span>' +
        '</button>' +
        '<button type="button" class="rf-resources-item rf-resources-item-add" data-action="add-file">' +
          '<span class="rf-resources-item-add-icon">⇪</span>' +
          '<span class="rf-resources-item-add-text"><strong>Add file</strong><em>Upload PDFs, slides, docs and more.</em></span>' +
        '</button>'
      : '';
    listEl.innerHTML = cards || (!adminCards ? '<div class="rf-resources-empty">No resources available yet.</div>' : '');
    if (viewerNotice) listEl.insertAdjacentHTML('beforeend', viewerNotice);
    if (adminCards) listEl.insertAdjacentHTML('beforeend', adminNotice + adminCards);
  }

  async function refreshRole(): Promise<void> {
    try {
      await Promise.resolve(roleService?.refresh?.());
    } catch {
      // best effort
    }
    if (!isResourceManager() && urlComposerOpen) setUrlComposerOpen(false);
  }

  function applyButtonVisibility(): void {
    const shouldShow = isResourceManager() || visibleResources.length > 0;
    btn.classList.toggle('rf-hidden', !shouldShow);
    if (!shouldShow && open) setOpen(false);
  }

  function bindListActions(): void {
    if (!listEl || !isResourceManager()) return;
    listEl.querySelectorAll('.rf-resource-toggle-visible').forEach((node) => {
      node.addEventListener('change', () => {
        const itemEl = (node as HTMLElement).closest('.rf-resources-item') as HTMLElement | null;
        const id = itemEl?.getAttribute('data-id');
        if (!id) return;
        const checked = (node as HTMLInputElement).checked;
        const labelEl = itemEl?.querySelector('.rf-resources-item-visible-label') as HTMLElement | null;
        if (labelEl) {
          labelEl.textContent = checked ? 'Visible' : 'Hidden';
        }
        Promise.resolve(storage.updateResource?.(adapter.getPresId(), id, { isVisible: checked })).catch((err) =>
          showError(err instanceof Error ? err.message : String(err))
        );
      });
    });
    listEl.querySelectorAll('.rf-resource-remove-icon').forEach((node) => {
      node.addEventListener('click', () => {
        const itemEl = (node as HTMLElement).closest('.rf-resources-item') as HTMLElement | null;
        const id = itemEl?.getAttribute('data-id');
        if (!id) return;
        showConfirmDialog('Remove this resource? This action cannot be undone.', 'Remove', () => {
          Promise.resolve(storage.removeResource?.(adapter.getPresId(), id)).catch((err) =>
            showError(err instanceof Error ? err.message : String(err))
          );
        });
      });
    });
    listEl.querySelectorAll('.rf-resource-edit-title-icon').forEach((node) => {
      node.addEventListener('click', () => {
        const itemEl = (node as HTMLElement).closest('.rf-resources-item') as HTMLElement | null;
        const id = itemEl?.getAttribute('data-id');
        if (!id) return;
        editingTitleId = id;
        renderList();
        bindListActions();
        const input = listEl.querySelector('.rf-resources-item[data-id="' + id + '"] .rf-resource-title-input') as HTMLInputElement | null;
        if (input) {
          input.focus();
          input.select();
        }
      });
    });
    listEl.querySelectorAll('.rf-resource-title-cancel').forEach((node) => {
      node.addEventListener('click', () => {
        editingTitleId = null;
        renderList();
        bindListActions();
      });
    });

    function saveInlineTitle(node: Element): void {
      const itemEl = node.closest('.rf-resources-item') as HTMLElement | null;
      const id = itemEl?.getAttribute('data-id');
      const input = itemEl?.querySelector('.rf-resource-title-input') as HTMLInputElement | null;
      if (!id || !input) return;
      const title = String(input.value || '').trim();
      if (!title) {
        showError('Title cannot be empty');
        input.focus();
        return;
      }
      if (title.length > 140) {
        showError('Title is too long (max 140 characters)');
        input.focus();
        return;
      }
      Promise.resolve(storage.updateResource?.(adapter.getPresId(), id, { title }))
        .then(() => {
          editingTitleId = null;
          renderList();
          bindListActions();
        })
        .catch((err) => showError(err instanceof Error ? err.message : String(err)));
    }

    listEl.querySelectorAll('.rf-resource-title-save').forEach((node) => {
      node.addEventListener('click', () => saveInlineTitle(node));
    });
    listEl.querySelectorAll('.rf-resource-title-input').forEach((node) => {
      node.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') {
          ev.preventDefault();
          saveInlineTitle(node);
          return;
        }
        if (ev.key === 'Escape') {
          ev.preventDefault();
          editingTitleId = null;
          renderList();
          bindListActions();
        }
      });
    });
    listEl.querySelectorAll('.rf-resource-move-up, .rf-resource-move-down').forEach((node) => {
      node.addEventListener('click', () => {
        const itemEl = (node as HTMLElement).closest('.rf-resources-item') as HTMLElement | null;
        const id = itemEl?.getAttribute('data-id');
        if (!id) return;
        const ordered = getSorted(allResources).map((r) => r.id);
        const idx = ordered.indexOf(id);
        if (idx < 0) return;
        const dir = (node as HTMLElement).classList.contains('rf-resource-move-up') ? -1 : 1;
        const target = idx + dir;
        if (target < 0 || target >= ordered.length) return;
        const tmp = ordered[idx];
        ordered[idx] = ordered[target];
        ordered[target] = tmp;
        Promise.resolve(storage.reorderResources?.(adapter.getPresId(), ordered)).catch((err) =>
          showError(err instanceof Error ? err.message : String(err))
        );
      });
    });
    listEl.querySelectorAll('.rf-resources-item-add').forEach((node) => {
      node.addEventListener('click', () => {
        const action = (node as HTMLElement).getAttribute('data-action');
        if (action === 'add-url') {
          setUrlComposerOpen(true);
          return;
        }
        if (action === 'add-file') {
          fileInput?.click();
        }
      });
    });
    const syncBtn = listEl.querySelector('.rf-resources-sync-ai-btn') as HTMLButtonElement | null;
    if (syncBtn) {
      syncBtn.addEventListener('click', () => {
        if (!ai?.requestReindex || syncInFlight) return;
        syncInFlight = true;
        syncBtn.disabled = true;
        setStatus('Syncing resources with AI…', 'default');
        const slidesText = collectSlidesPlainText();
        Promise.resolve(ai.requestReindex(adapter.getPresId(), { slidesText }))
          .then((result) => {
            const failed = Number(result?.failed || 0);
            const indexed = Number(result?.indexed || 0);
            if (result?.ok && failed === 0) {
              setStatus(`AI sync complete - indexed ${indexed} chunk${indexed === 1 ? '' : 's'}.`, 'success');
              // Optimistically mark all known resources as indexed; backend aiState will refine details.
              const now = Date.now();
              const nextState: Record<string, AIResourceSyncState> = { ...aiStateByResourceId };
              allResources.forEach((item) => {
                nextState[item.id] = {
                  resourceId: item.id,
                  status: 'indexed',
                  chunkCount: indexed > 0 ? indexed : nextState[item.id]?.chunkCount,
                  contentChars: nextState[item.id]?.contentChars,
                  isVisible: item.isVisible,
                  resourceType: item.type,
                  resourceTitle: item.title,
                  updatedAt: now,
                  indexedAt: now,
                  lastError: undefined
                };
              });
              aiStateByResourceId = nextState;
              return;
            }
            const firstError = Array.isArray(result?.errors) && result.errors.length ? String(result.errors[0].error || '').trim() : '';
            const baseWarning = 'Daily AI sync limit reached or sync failed for this presentation.';
            setStatus(firstError ? `${baseWarning} ${firstError}` : baseWarning, 'warning');
          })
          .catch((err) => {
            const msg = err instanceof Error ? err.message : String(err);
            const warning = /limit|resource-exhausted/i.test(msg)
              ? 'Daily AI sync limit reached for this presentation. Please try again tomorrow.'
              : (msg || 'AI sync failed');
            setStatus(warning, 'warning');
          })
          .finally(() => {
            syncInFlight = false;
            syncBtn.disabled = false;
            updateUploadStatus();
            void refreshQuotaSnapshot();
            renderList();
            bindListActions();
          });
      });
    }

    if (isResourceManager()) {
      void refreshQuotaSnapshot();
    }
  }

  async function refreshQuotaSnapshot(): Promise<void> {
    const presId = adapter.getPresId();
    const listElLocal = listEl;
    if (!listElLocal || !ai?.getQuotaSnapshot) return;
    const quotaEl = listElLocal.querySelector('.rf-resources-admin-quota') as HTMLElement | null;
    if (!quotaEl) return;
    try {
      quotaEl.textContent = 'Loading AI usage…';
      quotaEl.classList.add('rf-resources-admin-quota-loading');
      const data = await ai.getQuotaSnapshot(presId);
      const syncUsed = Math.max(0, data.sync.used);
      const syncLimit = Math.max(0, data.sync.limit);
      const queryUsed = Math.max(0, data.query.used);
      const queryLimit = Math.max(0, data.query.limit);

      const syncPct = syncLimit > 0 ? Math.min(100, Math.round((syncUsed / syncLimit) * 100)) : 0;
      const queryPct = queryLimit > 0 ? Math.min(100, Math.round((queryUsed / queryLimit) * 100)) : 0;

      const syncText = syncLimit > 0 ? `AI syncs: ${syncUsed}/${syncLimit} (${syncPct}%)` : 'AI syncs: unlimited';
      const queryText = queryLimit > 0 ? `AI questions: ${queryUsed}/${queryLimit} (${queryPct}%)` : 'AI questions: unlimited';

      const up = data.userProfile;
      const profileText =
        up && up.viewer.limit > 0 && up.presenter.limit > 0
          ? `Profile AI questions (UTC day): viewer ${up.viewer.used}/${up.viewer.limit} · presenter ${up.presenter.used}/${up.presenter.limit}`
          : '';

      quotaEl.textContent = profileText ? `${syncText} / ${queryText} · ${profileText}` : `${syncText} / ${queryText}`;
      quotaEl.classList.remove('rf-resources-admin-quota-loading');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      quotaEl.textContent = msg || 'Could not load AI usage.';
      quotaEl.classList.remove('rf-resources-admin-quota-loading');
    }
  }

  function handleResources(items: ResourceItem[]): void {
    allResources = getSorted(items);
    visibleResources = allResources.filter((r) => r.isVisible);
    applyButtonVisibility();
    renderList();
    bindListActions();
    updateUploadStatus();
  }

  function handleAIState(next: Record<string, AIResourceSyncState>): void {
    aiStateByResourceId = next;
    renderList();
    bindListActions();
    updateUploadStatus();
  }

  function resubscribe(): void {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    if (unsubscribeAIState) {
      unsubscribeAIState();
      unsubscribeAIState = null;
    }
    unsubscribe = storage.subscribeResources?.(
      adapter.getPresId(),
      (items) => {
        showError('');
        handleResources(items);
      },
      (err) => showError(err.message || 'Failed to load resources')
    ) || null;
    unsubscribeAIState = storage.subscribeAIState?.(
      adapter.getPresId(),
      (stateByResourceId) => {
        handleAIState(stateByResourceId);
      },
      () => {
        aiStateByResourceId = {};
        updateUploadStatus();
      }
    ) || null;
    if (!storage.subscribeResources) {
      handleResources(storage.getResources(adapter.getPresId()));
    }
    if (!storage.subscribeAIState) {
      handleAIState(storage.getAIState?.(adapter.getPresId()) || {});
    }
  }

  btn.addEventListener('click', () => setOpen(!open));
  closeBtn?.addEventListener('click', () => setOpen(false));
  backdrop.addEventListener('click', () => setOpen(false));
  window.__lecturaToggleResources = () => setOpen(!open);
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && open) setOpen(false);
  });

  async function resolveComposerPreview(force = false): Promise<UrlPreviewData | null> {
    const raw = String(urlInput?.value || '').trim();
    if (!raw) {
      setComposerPreview(null);
      return null;
    }
    try {
      const normalized = normalizeHttpUrl(raw);
      if (!force && activeUrlPreview && activeUrlPreview.normalizedUrl === normalized) {
        return activeUrlPreview;
      }
      const meta = await fetchUrlPreview(raw, adapter.getPresId());
      setComposerPreview(meta);
      return meta;
    } catch (err) {
      showError(err instanceof Error ? err.message : String(err));
      return null;
    }
  }

  urlInput?.addEventListener('blur', () => {
    void resolveComposerPreview(false);
  });

  urlInput?.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter') return;
    ev.preventDefault();
    if (addUrlBtn?.disabled) return;
    addUrlBtn?.click();
  });

  addUrlBtn?.addEventListener('click', async () => {
    if (!urlInput) return;
    showError('');
    addUrlBtn.disabled = true;
    setStatus('Fetching website details…');
    const meta = await resolveComposerPreview(true);
    if (!meta) {
      addUrlBtn.disabled = false;
      updateUploadStatus();
      return;
    }
    Promise.resolve(storage.addUrl?.(adapter.getPresId(), {
      title: meta.title || meta.normalizedUrl,
      description: meta.description,
      url: meta.normalizedUrl,
      isVisible: false
    }))
      .then(() => {
        setUrlComposerOpen(false);
      })
      .catch((err) => showError(err instanceof Error ? err.message : String(err)))
      .finally(() => {
        addUrlBtn.disabled = false;
        updateUploadStatus();
      });
  });
  cancelUrlBtn?.addEventListener('click', () => setUrlComposerOpen(false));

  function uploadFile(file: File): void {
    uploadInFlight += 1;
    updateUploadStatus();
    Promise.resolve(storage.addFile?.(adapter.getPresId(), file, { isVisible: false }))
      .catch((err) => showError(err instanceof Error ? err.message : String(err)))
      .finally(() => {
        uploadInFlight = Math.max(0, uploadInFlight - 1);
        updateUploadStatus();
      });
  }

  fileInput?.addEventListener('change', () => {
    const file = fileInput.files && fileInput.files[0];
    if (file) uploadFile(file);
    if (fileInput) fileInput.value = '';
  });
  drawer.addEventListener('dragover', (ev) => {
    ev.preventDefault();
    if (!isResourceManager()) return;
    drawer.classList.add('rf-resources-upload-dragover');
  });
  drawer.addEventListener('dragleave', () => drawer.classList.remove('rf-resources-upload-dragover'));
  drawer.addEventListener('drop', (ev) => {
    ev.preventDefault();
    drawer.classList.remove('rf-resources-upload-dragover');
    if (!isResourceManager()) return;
    const file = ev.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  });

  on(EVENTS.USER_CHANGED, () => {
    refreshRole().finally(() => {
      // Auth changes can invalidate Firestore listeners (permission-denied on initial load).
      // Re-subscribe so resources become visible immediately after sign-in without a full page refresh.
      resubscribe();
      applyButtonVisibility();
      renderList();
      bindListActions();
    });
  });

  roleService?.subscribe?.(() => {
    applyButtonVisibility();
    renderList();
    bindListActions();
  });

  if (opts.sessionCanManageResources) {
    const onRemoteSessionUpdated = (): void => {
      applyButtonVisibility();
      renderList();
      bindListActions();
    };
    window.addEventListener('lectura-remote-session-updated', onRemoteSessionUpdated);
  }

  refreshRole().finally(() => {
    resubscribe();
    applyButtonVisibility();
  });
}
